import { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { SPACING_LARGE } from '../constant/spacing';
import { Grid, Card, Typography, MenuItem, InputLabel, Select, FormControl } from '@material-ui/core';
import { Organization } from '@kaizenplatform/intelligence-entity';
import ChartGroup from '../../entity/ChartGroup';
import Loading from '../component/compound/Loading';
import CustomReportChart from '../component/compound/CustomReportChart/CustomReportChart';
import GlobalPeriod from '../../entity/GlobalPeriod';
import { ChartBreakdownRequest } from '@kaizenplatform/kaizen-chartjs';
import Breakdown from '../../entity/Breakdown';

type Props = {
  organization: Organization | null;
  chartGroup?: ChartGroup;
  customReportChartSetting: GlobalPeriod;
  customReportLoading: boolean;
  onMount: () => void;
  onUnmount: () => void;
  className?: string;
};

const CustomReportPage: FC<Props> = ({
  organization,
  chartGroup,
  customReportChartSetting,
  customReportLoading,
  onMount,
  onUnmount,
  className,
}) => {
  const [globalBreakdownRequests, setGlobalBreakdownRequests] = useState<ChartBreakdownRequest[]>();
  const [globalBreakdowns, setGlobalBreakdowns] = useState<Breakdown[]>();
  const NOTHING = 'NOTHING';

  useEffect(() => {
    if (organization) {
      onMount();
    }

    return () => {
      onUnmount();
    };
  }, [organization]);

  useEffect(() => {
    if (globalBreakdowns !== undefined) {
      setGlobalBreakdownRequests(
        globalBreakdowns
          .map((item) => {
            if (item.value === NOTHING) {
              return;
            } else return new ChartBreakdownRequest({ breakdown: item.type, filter: [item.value] });
          })
          .filter((item) => item) as ChartBreakdownRequest[],
      );
    }
  }, [globalBreakdowns]);

  useEffect(() => {
    if (chartGroup) {
      setGlobalBreakdowns(
        chartGroup.breakdowns.filter(
          (item, index, self) => self.findIndex((breakdown) => breakdown.type === item.type) === index,
        ),
      );
    }
  }, [chartGroup]);

  if (chartGroup !== undefined && globalBreakdowns === undefined) {
    setGlobalBreakdowns(chartGroup.breakdowns);
  }

  if (globalBreakdownRequests === undefined && globalBreakdowns) {
    setGlobalBreakdownRequests(
      globalBreakdowns.map((item) => {
        return new ChartBreakdownRequest({ breakdown: item.type, filter: [item.value] });
      }),
    );
  }

  const onChangeGlobalBreakdowns = (value: string, type: string) => {
    const newBreakdown = chartGroup?.breakdowns.find((item) => item.value === value);

    if (newBreakdown && globalBreakdowns) {
      const updatingIndex = globalBreakdowns.findIndex((item) => item.type === type);
      const newBreakdowns = globalBreakdowns.map((item, index) => {
        if (index === updatingIndex) {
          return newBreakdown;
        } else return item;
      });

      setGlobalBreakdowns(newBreakdowns);
    } else if (globalBreakdowns && value === NOTHING) {
      const updatingIndex = globalBreakdowns.findIndex((item) => item.type === type);
      const newBreakdowns = globalBreakdowns
        .map((item, index) => {
          if (index === updatingIndex) {
            return new Breakdown({ type: item.type, label: NOTHING, value: NOTHING });
          } else return item;
        })
        .filter((item) => item);

      setGlobalBreakdowns(newBreakdowns);
    }
  };

  return (
    <Root className={className}>
      <Container>
        {chartGroup && globalBreakdowns
          ? globalBreakdowns.map((item) => {
              const values = chartGroup.breakdowns.filter((breakdown) => breakdown.type === item.type);
              const menuItems = values.map((value) => {
                return (
                  <MenuItem value={value.value} key={value.value}>
                    <Typography>{value.label}</Typography>
                  </MenuItem>
                );
              });
              return (
                <CustomFormControl key={item.type}>
                  <InputLabel>{item.type}</InputLabel>
                  <Select
                    value={item.value}
                    onChange={(e) => onChangeGlobalBreakdowns(e.target.value as string, item.type)}
                  >
                    <MenuItem value={'NOTHING'} key={'NOTHING'}>
                      <Typography>{'未選択'}</Typography>
                    </MenuItem>
                    {menuItems}
                  </Select>
                </CustomFormControl>
              );
            })
          : null}
        {customReportLoading ? (
          <Grid item lg={12} sm={12} xs={12}>
            <LoadingArea>
              <Loading />
            </LoadingArea>
          </Grid>
        ) : chartGroup ? (
          <Grid container spacing={4}>
            {chartGroup.charts.map((id) => (
              <Grid item lg={12} sm={12} xs={12} key={id}>
                <Card>
                  <CustomReportChart
                    chartId={id}
                    customReportChartSetting={customReportChartSetting}
                    customReportLoading={customReportLoading}
                    globalBreakdownRequests={globalBreakdownRequests}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid item lg={12} sm={12} xs={12}>
            <Card>
              <ErrorTextArea>
                <Typography>カスタムレポートがありません</Typography>
              </ErrorTextArea>
            </Card>
          </Grid>
        )}
      </Container>
    </Root>
  );
};

const Root = styled('div')({});

const CustomFormControl = styled(FormControl)({
  minWidth: 120,
});

const LoadingArea = styled('div')({
  height: '540px',
});

const ErrorTextArea = styled('div')({
  height: '540px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const Container = styled('div')({
  padding: SPACING_LARGE,
});

export default CustomReportPage;
